import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Form } from "antd";
import { useDispatch } from "react-redux";

// Shared UI Library Components
import {
  CustomButton,
  DropdownElement,
  CustomInput,
} from "@ppmcore/seven-ppm-core-shared-components-react";

// Styles
import './booking-cancellation-time.scss';

// Store
import { updateAppointmentsSettings } from "../../../../../store/session-settings/session-settings.thunks";

// Models
import { IAppointmentSettings } from "../../../../../interfaces/session-settings.interfaces";

const minimumNoticeOptions = [
  { value: 'minutes', label: `minutes` },
  { value: 'hours', label: `hours` },
  { value: 'days', label: `days` },
];

type TFormValue = {
  max_user_cancellation_hours: string;
  units: string;
};

export type TBookingCancellationTimeRef = {
  isHasChanges: () => boolean;
  resetState: () => void;
};

type TBookingCancellationTimeProps = {
  appointmentsSettings: IAppointmentSettings;
};

export const BookingCancellationTime = forwardRef<TBookingCancellationTimeRef, TBookingCancellationTimeProps>((
  { appointmentsSettings }: TBookingCancellationTimeProps, _ref
) => {
  const [form] = Form.useForm<TFormValue>();
  const formValue = Form.useWatch([], form);
  const unitsValue = Form.useWatch('units', form);

  const getInitialValueObject = (): { max_user_cancellation_hours: string, units: string, viewUnits: string } => {
    if (appointmentsSettings.max_user_cancellation_hours >= 1440 && appointmentsSettings.max_user_cancellation_hours % 24 === 0) {
      const value = Math.floor(appointmentsSettings.max_user_cancellation_hours / (24 * 60));
      return { max_user_cancellation_hours: `${ value || 0 }`, units: 'days', viewUnits: value === 1 ? 'day' : 'days' };
    }
    if (appointmentsSettings.max_user_cancellation_hours >= 60 && appointmentsSettings.max_user_cancellation_hours % 60 === 0) {
      const value = Math.floor(appointmentsSettings.max_user_cancellation_hours / 60);
      return { max_user_cancellation_hours: `${ value || 0 }`, units: 'hours', viewUnits: value === 1 ? 'hour' : 'hours' };
    }
    const value = appointmentsSettings.max_user_cancellation_hours;
    return { max_user_cancellation_hours: `${ value || 0 }`, units: 'minutes', viewUnits: value === 1 ? 'minute' : 'minutes' };
  }

  const maxValue = useMemo(() => {
    if (unitsValue === 'minutes') return 43200;
    if (unitsValue === 'hours') return 720;
    return 30;
  }, [unitsValue]);
  const initialValues = useMemo(() => getInitialValueObject(), [appointmentsSettings]);
  const errorMessage = useMemo(() => {
    if (!formValue) return;
    if (formValue.units === 'days' && +formValue.max_user_cancellation_hours <= 0) {
      return 'Must be greater than 1';
    }
    if (formValue.units === 'days' && +formValue.max_user_cancellation_hours > 10) {
      return 'Must be greater than 1 and less then 10';
    }
    if (formValue.units === 'hours' && +formValue.max_user_cancellation_hours <= 0) {
      return 'Must be greater than 1';
    }
    if (formValue.units === 'hours' && +formValue.max_user_cancellation_hours > 240) {
      return 'Must be greater than 1 and less then 240';
    }
    if (formValue.units === 'minutes' && +formValue.max_user_cancellation_hours < 30) {
      return 'Must be greater than 30';
    }
    if (formValue.units === 'minutes' && +formValue.max_user_cancellation_hours > 14400) {
      return 'Must be greater than 30 and less then 14400';
    }
    return '';
  }, [formValue]);

  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const dispatch = useDispatch<any>();

  const onValueChangesHandler = (_: { [key: string]: keyof TFormValue }, { max_user_cancellation_hours, units }: TFormValue): void => {
    setHasChanges(+max_user_cancellation_hours != appointmentsSettings.max_user_cancellation_hours || initialValues.units !== units);
  }

  const getValueInMinutesByUnit = (minimum_notice: string, unit: string): number => {
    if (unit === 'minutes') {
      return +minimum_notice;
    }
    if (unit === 'hours') {
      return +minimum_notice * 60;
    }
    return +minimum_notice * 60 * 24;
  }

  const onSaveChangesHandler = async (): Promise<void> => {
    const { max_user_cancellation_hours, units } = form.getFieldsValue();
    const valueInMinutes = getValueInMinutesByUnit(max_user_cancellation_hours, units);
    setLoading(true);
    const { payload } = await dispatch(updateAppointmentsSettings({ value: valueInMinutes, key: 'max_user_cancellation_hours' }));
    setLoading(false);

    if (!payload) return;
    setHasChanges(false);
  }

  useImperativeHandle(_ref, () => ({
    isHasChanges(): boolean {
      return hasChanges;
    },
    resetState(): void {
      form.setFieldsValue(initialValues);
      setHasChanges(false);
      setLoading(false);
    }
  }));

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [appointmentsSettings]);

  return (<div className="booking-cancellation-time">

    <div className="booking-cancellation-time--header">
      <div className="header-title">
        Booking Cancellation Time
      </div>
    </div>

    <div className="booking-cancellation-time--description">
      Set the time during which the Client and the Consultant cannot cancel the reservation before the appointment starts.
    </div>

    <Form form={ form } onValuesChange={ onValueChangesHandler } className="booking-cancellation-time--body">

      <Form.Item className="body-item" name={ 'max_user_cancellation_hours' }
                 validateStatus={ errorMessage ? 'error' : 'success' }>
        <CustomInput type={ 'number' } min={ 1 } max={ maxValue }
                     placeholder={ 'Cancellation Time' }/>
      </Form.Item>

      <Form.Item className="body-item" name={ 'units' }>
        <DropdownElement options={ minimumNoticeOptions } withoutAll={ true } placeholder={ 'Units' }/>
      </Form.Item>

      { hasChanges && <div className="body-item item-actions">

          <CustomButton size={ 'small' }
                        loading={ loading } disabled={ !!errorMessage }
                        onClick={ onSaveChangesHandler }>
            Save
          </CustomButton>
      </div> }

      { errorMessage && <div className="body-item error-message">
        { errorMessage }
      </div> }

    </Form>
  </div>);
});
